// Bespoke client for the admin-users API (/api/admin-users) : Auth0-backed in prod, local DB in dev.
// Hand-written (lives beside its consumer; `src/API/` is regenerated). Uses the shared axios
// instance so the admin token is attached by the Auth0AxiosBridge interceptor.
import axios from "axios";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

export interface AdminUser {
	id: string;
	email: string;
	name?: string | null;
	picture?: string | null;
	/** Storage key of the avatar uploaded in the admin : wins over `picture`. */
	avatarKey?: string | null;
	blocked: boolean;
	roles: string[];
	lastLogin?: string | null;
	/** Dernier changement de mot de passe (Auth0 last_password_reset) : libre-service,
	 *  e-mail de réinitialisation ou forçage administrateur. Null si jamais changé
	 *  ou annuaire local. */
	lastPasswordReset?: string | null;
	/** Faux pour un compte arrivé UNIQUEMENT par connexion sociale (Google, ...) : pas de mot
	 *  de passe applicatif, donc ni colonne « Mot de passe modifié » pertinente, ni forçage,
	 *  ni e-mail de réinitialisation. Absent (ancienne API) = true. */
	hasPassword?: boolean;
	createdAt?: string | null;
	source: string;
	/** Nombre de connexions enregistrées par l'annuaire (Auth0 logins_count) ; absent si inconnu. */
	loginsCount?: number | null;
	/** IP de la dernière connexion (Auth0 last_ip) ; absent si inconnu. */
	lastIp?: string | null;
	/** E-mail vérifié selon l'annuaire ; absent si inconnu. */
	emailVerified?: boolean | null;
	/** Les identités (connexions) derrière cet enregistrement : base (mot de passe), Google… */
	identities?: Array<{ provider: string; connection?: string | null; isSocial: boolean }>;
}

export interface AssignableRole {
	key: string;
	name: string;
	description?: string | null;
	isSystem: boolean;
	/** Jetons de grant BRUTS du rôle (wildcards/groupes compris) : expansés côté client via
	 *  expandGrants pour comparer au set de l'appelant (règle de sous-ensemble à l'attribution). */
	permissions?: string[];
	/** Pastille d'affichage du rôle (mêmes métadonnées que la barre du haut) ; null pour un rôle
	 *  jamais montré (bcc-internal). */
	badge?: { roleKey: string; label: string; color: string; kind: string } | null;
}

export interface CreateUserBody {
	email: string;
	name?: string;
	roles: string[];
}

export interface UpdateUserBody {
	name?: string;
	blocked?: boolean;
	roles?: string[];
}

export const usersClient = {
	async list(search?: string): Promise<{ source: string; users: AdminUser[] }> {
		const r = await axios.get(`${apiBase}/api/admin-users`, {
			params: search ? { search } : undefined,
			withCredentials: true
		});
		return r.data ?? { source: "local", users: [] };
	},
	async assignableRoles(): Promise<AssignableRole[]> {
		const r = await axios.get<AssignableRole[]>(`${apiBase}/api/admin-users/assignable-roles`, { withCredentials: true });
		return r.data ?? [];
	},
	async create(body: CreateUserBody): Promise<AdminUser> {
		const r = await axios.post<AdminUser>(`${apiBase}/api/admin-users`, body, { withCredentials: true });
		return r.data;
	},
	async update(id: string, body: UpdateUserBody): Promise<AdminUser> {
		const r = await axios.put<AdminUser>(`${apiBase}/api/admin-users/${encodeURIComponent(id)}`, body, { withCredentials: true });
		return r.data;
	},
	async remove(id: string): Promise<void> {
		await axios.delete(`${apiBase}/api/admin-users/${encodeURIComponent(id)}`, { withCredentials: true });
	},
	async resendInvite(id: string): Promise<void> {
		await axios.post(`${apiBase}/api/admin-users/${encodeURIComponent(id)}/resend-invite`, {}, { withCredentials: true });
	},
	/** Action administrateur : redéfinit le mot de passe d'un AUTRE compte sans connaître
	 *  l'actuel. Refusé par le serveur sur soi-même (passer par « Mon compte ») et sur un
	 *  super-admin quand l'appelant ne l'est pas. */
	async forcePassword(id: string, newPassword: string): Promise<void> {
		await axios.post(`${apiBase}/api/admin-users/${encodeURIComponent(id)}/force-password`, { newPassword }, { withCredentials: true });
	}
};
